import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { useToast } from '@/shared/contexts/ToastContext'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'
import {
  patchTracingReportItem,
  type TracingReportItem,
} from '@/features/internal/tracing-report'
import { useLineEditing } from '@/apps/latest/components/organisms/PostRecordingView/hooks/useLineEditing'

// トレーシングレポートの 1 行編集 / 行追加 / チェック (採否) を SOAP・訪問報告書と同じ操作感で
// 扱う hook (useHomeVisitReportEditing と同型)。key = TracingReportItem.id で、item ごとの
// report_json を PATCH 保存し items query を invalidate する。

interface Args {
  yakurekiId: string
  items: TracingReportItem[]
}

export const useTracingReportEditing = ({ yakurekiId, items }: Args) => {
  const { showToast } = useToast()
  const queryClient = useQueryClient()

  const itemsOf = useCallback(
    (itemId: string): SoapContentItem[] =>
      items.find((item) => item.id === itemId)?.report_json ?? [],
    [items]
  )

  const commit = useCallback(
    async (itemId: string, nextItems: SoapContentItem[]) => {
      try {
        await patchTracingReportItem(itemId, nextItems)
        await queryClient.invalidateQueries({
          queryKey: queryKeys.tracingReport.items(yakurekiId),
        })
        return true
      } catch {
        showToast('保存に失敗しました', 'error')
        return false
      }
    },
    [yakurekiId, queryClient, showToast]
  )

  const line = useLineEditing<string>({ itemsOf, commit })

  // checkbox (採否 = is_selected) トグル。
  const toggleSelect = useCallback(
    (itemId: string, index: number) => {
      const nextItems = [...itemsOf(itemId)]
      const item = nextItems[index]
      if (!item) return
      nextItems[index] = {
        ...item,
        is_selected: !(item.is_selected ?? true),
      }
      void commit(itemId, nextItems)
    },
    [itemsOf, commit]
  )

  return {
    ...line,
    toggleSelect,
  }
}

export type TracingReportEditing = ReturnType<typeof useTracingReportEditing>
